import * as React from 'react';
import { Platform } from 'react-native';
import GrainTypeDropdown from '../../components/dropdown/GrainTypeDropdown';
import GrainProfileDropdown from '../../components/dropdown/GrainProfileDropdown';
import { useDispatch, useSelector } from "react-redux";
import { Box, Button } from "native-base";
import { IStore } from '../../redux/reducers';
import { containerStyle } from 'src/styles/containerStyle';
import ProgressBar from 'src/components/ProgressBar';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { getText } from 'src/utils/i18n';
import { isLightboxFeatureEnabled } from 'src/utils/grainFeatureUtils';
import * as GrainTypeActions from '../../redux/actions/grainTypeAction';
import * as GrainProfileActions from '../../redux/actions/grainProfileAction';
import { LAST_GRAIN_PROFILE_STORAGE_KEY } from 'src/constants';
import { getOperatorCaptureMode } from 'src/utils/operatorMode';

export default function ChooseGrainProfilePage({ route, navigation }) {
    const dispatch = useDispatch();
    const [lastUsedSelection, setLastUsedSelection] = React.useState<{ grainId: string; profileId: string }>();
    const [profileStorageLoaded, setProfileStorageLoaded] = React.useState(false);
    const clearedInitialProfileRef = React.useRef(false);
    const restoredLastUsedSelectionKeyRef = React.useRef<string>();
    const isFetchingGrainType = useSelector((store: IStore) => store.user.userProfile.isFetching);
    const grainId = useSelector((store: IStore) => store.grainType.grainId);
    const profileId = useSelector((store: IStore) => store.grainProfile.profileId);
    const lightboxEnabled = useSelector((store: IStore) =>
        isLightboxFeatureEnabled(store.user.userProfile.response?.settings)
    );
    const grainProfiles = useSelector((store: IStore) => store.grainProfile.grainProfiles);
    const subscriptions = useSelector((store: IStore) => store.user.userProfile.response?.subscriptions) || [];
    const currentGrainProfiles = React.useMemo(
        () => (grainProfiles || []).filter((profile) => profile.grainId === grainId),
        [grainProfiles, grainId]
    );

    React.useEffect(() => {
        let active = true;
        AsyncStorage.getItem(LAST_GRAIN_PROFILE_STORAGE_KEY)
            .then(value => {
                if (!active || !value) return;
                const saved = JSON.parse(value);
                if (!saved?.grainId || !saved?.profileId) return;
                setLastUsedSelection(saved);
                dispatch(GrainTypeActions.setGrainId(String(saved.grainId)));
                dispatch(GrainProfileActions.setProfileId(String(saved.profileId)));
            })
            .catch(error => console.warn('Could not restore the last grain profile:', error))
            .finally(() => {
                if (active) setProfileStorageLoaded(true);
            });
        return () => { active = false; };
    }, [dispatch]);

    React.useEffect(() => {
        if (!profileStorageLoaded || lastUsedSelection || currentGrainProfiles.length === 0 || clearedInitialProfileRef.current) {
            return;
        }
        clearedInitialProfileRef.current = true;
        dispatch(GrainProfileActions.setProfileId(''));
    }, [currentGrainProfiles.length, dispatch, lastUsedSelection, profileStorageLoaded]);

    React.useEffect(() => {
        if (!lastUsedSelection || String(lastUsedSelection.grainId) !== String(grainId)) return;
        const savedSelectionKey = `${lastUsedSelection.grainId}:${lastUsedSelection.profileId}`;
        if (restoredLastUsedSelectionKeyRef.current === savedSelectionKey) return;
        const savedProfileExists = currentGrainProfiles.some(
            profile => String(profile.profileId) === String(lastUsedSelection.profileId)
        );
        if (!savedProfileExists) return;

        restoredLastUsedSelectionKeyRef.current = savedSelectionKey;
        if (String(profileId) !== String(lastUsedSelection.profileId)) {
            dispatch(GrainProfileActions.setProfileId(String(lastUsedSelection.profileId)));
        }
    }, [currentGrainProfiles, dispatch, grainId, lastUsedSelection, profileId]);
    const handleSaveProfile = async () => {
        if (!profileId || currentGrainProfiles.length === 0) {
            return;
        }

        const isValidProfile = currentGrainProfiles.some(profile => profile.profileId === profileId);
        if (!isValidProfile) {
            return;
        }

        const savedSelection = { grainId: String(grainId), profileId: String(profileId) };
        await AsyncStorage.setItem(LAST_GRAIN_PROFILE_STORAGE_KEY, JSON.stringify(savedSelection));
        restoredLastUsedSelectionKeyRef.current = `${savedSelection.grainId}:${savedSelection.profileId}`;
        setLastUsedSelection(savedSelection);
        if (Platform.OS === 'web') {
            // Check AsyncStorage for new signup flag
            const isNewSignupFlag = await AsyncStorage.getItem('isNewSignup');
            const isNewSignup = isNewSignupFlag === 'true';

            // Clear the flag after checking
            if (isNewSignup) {
                await AsyncStorage.removeItem('isNewSignup');
            }

            // For new signups, always redirect to manage-subscription page
            // This gives them the option to upgrade from their Basic/Free tier
            if (isNewSignup) {
                navigation.navigate('ManageUserSubscription');
            } else {
                navigation.navigate('Home');
            }
        } else {
            const selectedProfile = currentGrainProfiles.find(profile => profile.profileId === profileId);
            const captureMode = getOperatorCaptureMode({ selectedProfile, subscriptions });
            if (route.params !== undefined && route.params.returnBackTo !== undefined) {
                navigation.navigate(route.params.returnBackTo);
            } else if (captureMode === 'CAMERA' || lightboxEnabled) {
                navigation.navigate('Home');
            } else {
                navigation.navigate('ConnectToRpi');
            }
        }
    }

    const isButtonDisabled = !profileId || currentGrainProfiles.length === 0 ||
        !currentGrainProfiles.some(profile => profile.profileId === profileId);

    return (
        <Box variant={"main"} style={containerStyle.mainContainer}>
            <ProgressBar isVisible={isFetchingGrainType || false} progressText={getText('loading')} />
            <GrainTypeDropdown />
            <GrainProfileDropdown />
            <Button 
                style={{
                    marginTop: 24,
                }}
                isDisabled={isButtonDisabled}
                onPress={handleSaveProfile}>SAVE PROFILE</Button>
        </Box>
    );
}
